import { AdminHeaderActionButton } from '@/components/patterns/admin-page'
import { useRotatingIdempotencyKey } from '@/components/pos/use-rotating-idempotency-key'
import { CashDrawerDialogHost, useCashDrawerControls } from '@/components/pos/CashDrawerControls'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Typography } from '@/components/ui/typography'
import { Can } from '@/lib/casl'
import { usePathHelper } from '@/hooks/usePathHelper'
import { TillOpenSince } from '@/components/pos/TillOpenSince'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import { Link } from '@tanstack/react-router'
import {
  Banknote,
  BarChart3,
  ChevronDown,
  MinusCircle,
  MonitorCog,
  PlusCircle,
  RefreshCw,
} from 'lucide-react'
import { toast } from 'sonner'

export { useRotatingIdempotencyKey }

export function CashDrawerMenu() {
  return (
    <Can do="operate" on="cash-drawers">
      <CashDrawerMenuContent />
    </Can>
  )
}

function CashDrawerMenuContent() {
  const orgId = useRouteOrganizationId()
  const path = usePathHelper()
  const controls = useCashDrawerControls(orgId)
  const {
    cashDrawerStatusQuery,
    currentDrawerQuery,
    currentSession,
    initialLoadFailed,
    openDialog,
    pairedWorkstation,
    trackingEnabled,
    workstations,
    workstationsQuery,
  } = controls
  const handleInitialRetry = async () => {
    const result = await workstationsQuery.refetch()

    if (result.isError) {
      toast.error('Unable to load cash drawers', {
        description: 'Check your connection and try again.',
      })
    }
  }
  // Another device may have started or counted the drawer, or an admin changed the register or
  // tracking. The menu shows the cached state right away and quietly rechecks whatever is stale.
  const handleMenuOpenChange = (open: boolean) => {
    if (!open) return

    for (const query of [cashDrawerStatusQuery, workstationsQuery, currentDrawerQuery]) {
      if (query.isStale) void query.refetch()
    }
  }

  if (initialLoadFailed) {
    return (
      <AdminHeaderActionButton
        type="button"
        variant="outline"
        className="gap-2"
        disabled={workstationsQuery.isFetching}
        onClick={handleInitialRetry}
      >
        <RefreshCw className={workstationsQuery.isFetching ? 'size-4 animate-spin' : 'size-4'} />
        {workstationsQuery.isFetching ? 'Retrying cash drawer…' : 'Retry cash drawer'}
      </AdminHeaderActionButton>
    )
  }

  if (
    workstationsQuery.isLoading ||
    cashDrawerStatusQuery.isLoading ||
    // Wait for the paired drawer too, so the menu never opens with the wrong actions.
    currentDrawerQuery.isLoading ||
    workstations.length === 0 ||
    !trackingEnabled
  ) {
    return null
  }

  if (!pairedWorkstation) {
    return (
      <>
        <AdminHeaderActionButton
          type="button"
          variant="outline"
          className="gap-2"
          onClick={() => openDialog({ type: 'pair' })}
        >
          <MonitorCog className="size-4" /> Set up register
        </AdminHeaderActionButton>
        <CashDrawerDialogHost
          controls={controls}
          pairSuccessMessage="This device is ready for register use"
        />
      </>
    )
  }

  return (
    <>
      <DropdownMenu onOpenChange={handleMenuOpenChange}>
        <DropdownMenuTrigger asChild>
          <AdminHeaderActionButton type="button" variant="outline" className="gap-2">
            <Banknote className="size-4" />
            Cash Drawer
            <ChevronDown className="size-4" />
          </AdminHeaderActionButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" size="lg">
          <DropdownMenuLabel>
            <span className="block">{pairedWorkstation.name}</span>
            <Typography variant="supporting" className="block">
              {pairedWorkstation.location.name}
              {currentSession ? '' : ' · Cash drawer not started'}
            </Typography>
            {currentSession ? (
              <TillOpenSince openedAt={currentSession.opening.recorded_at} />
            ) : null}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {currentSession ? (
            <>
              <DropdownMenuItem
                onSelect={() => openDialog({ type: 'close', session: currentSession })}
              >
                <MinusCircle className="size-4" /> Count cash drawer
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => openDialog({ type: 'adjust', session: currentSession })}
              >
                <PlusCircle className="size-4" /> Add or remove cash
              </DropdownMenuItem>
            </>
          ) : (
            <DropdownMenuItem
              onSelect={() => openDialog({ type: 'start', workstationId: pairedWorkstation.id })}
            >
              <Banknote className="size-4" /> Start cash drawer
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => openDialog({ type: 'pair' })}>
            <MonitorCog className="size-4" /> Change this device&apos;s register
          </DropdownMenuItem>
          <Can do="list" on="cash-drawer-reports">
            <DropdownMenuItem asChild>
              <Link {...path.cashDrawer.index().using({ orgId }).link}>
                <BarChart3 className="size-4" /> Cash Drawer Report
              </Link>
            </DropdownMenuItem>
          </Can>
        </DropdownMenuContent>
      </DropdownMenu>

      <CashDrawerDialogHost
        controls={controls}
        pairSuccessMessage="This device now uses the selected register"
      />
    </>
  )
}
